import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { attribution } from "@/lib/attribution";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PhoneVerify from "@/components/PhoneVerify";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Video, MapPin, CheckCircle2 } from "lucide-react";
import { useContent } from "@/lib/content";

const FALLBACK_SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM"];

export default function Appointments() {
  const { user } = useAuth();
  const { c } = useContent();
  // The clinic can block dates/slots from Admin -> Availability, so the bookable
  // slots always come from the API for the chosen date.
  const [slots, setSlots] = useState(FALLBACK_SLOTS.map((s) => ({ slot: s, available: true, reason: "" })));
  const [dayBlocked, setDayBlocked] = useState(false);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState("");
  const [type, setType] = useState("in-person");
  const [reason, setReason] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phoneToken, setPhoneToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [confirmedApt, setConfirmedApt] = useState(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/appointments");
      setMine(data);
    } catch (e) {
      console.error("load appointments failed:", e);
    }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line

  useEffect(() => {
    const day = format(date, "yyyy-MM-dd");
    api.get("/appointments/availability", { params: { date: day } })
      .then(({ data }) => {
        setSlots(data.slots);
        setDayBlocked(data.day_blocked);
        setSlot((current) => (data.slots.find((s) => s.slot === current && s.available) ? current : ""));
      })
      .catch(() => {});
  }, [date]);

  const canSubmit = user
    ? Boolean(slot && reason && name)
    : Boolean(slot && reason && name && phone && phoneToken);

  const book = async () => {
    if (!slot || !reason) return toast.error("Pick a time slot and add a reason.");
    if (!user && !phoneToken) return toast.error("Please verify your phone number with OTP.");
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", {
        date: format(date, "yyyy-MM-dd"),
        time_slot: slot,
        reason,
        consultation_type: type,
        customer_name: name,
        customer_email: email,
        customer_phone: phone,
        phone_token: phoneToken,
        ...attribution(),
      });
      toast.success("Appointment requested. We'll confirm shortly.");
      setConfirmedApt(data);
      setReason("");
      setSlot("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (confirmedApt) {
    return (
      <main className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="appt-success">
        <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-6">
          <CheckCircle2 className="text-primary" size={40} />
        </div>
        <div className="overline mb-2">Appointment Requested</div>
        <h1 className="font-display text-4xl lg:text-5xl mb-3">Namaste!</h1>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Your consultation on <b>{confirmedApt.date}</b> at <b>{confirmedApt.time_slot}</b> has been received.
          Our team will confirm on <b>{confirmedApt.user_phone}</b> shortly.
        </p>
        <Button onClick={()=>setConfirmedApt(null)} className="btn-pill bg-primary text-primary-foreground" data-testid="appt-book-another">Book Another</Button>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="overline mb-3" data-testid="appt-overline">{c("appointments.hero.overline")}</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-2" data-testid="appt-title">{c("appointments.hero.title")}</h1>
      <p className="text-muted-foreground mb-10 max-w-xl">{c("appointments.hero.subtitle")}</p>

      <div className="grid lg:grid-cols-5 gap-8">
        <Card className="lg:col-span-3 border-border">
          <CardContent className="p-6 lg:p-8 space-y-6">
            <div>
              <Label className="overline">Select Date</Label>
              <div className="mt-3 border border-border rounded-xl p-3 inline-block bg-card">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} fromDate={new Date()} data-testid="appt-calendar" />
              </div>
            </div>
            <div>
              <Label className="overline">Available Slots</Label>
              {dayBlocked && (
                <p className="text-xs text-destructive mt-2" data-testid="appt-day-blocked">
                  The clinic is closed on this date — please choose another day.
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                {slots.map((s) => (
                  <button
                    key={s.slot}
                    type="button"
                    disabled={!s.available}
                    title={s.reason}
                    onClick={() => setSlot(s.slot)}
                    data-testid={`slot-${s.slot}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm transition-all ${
                      !s.available
                        ? "border-border/60 text-muted-foreground/50 line-through cursor-not-allowed"
                        : slot === s.slot
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border hover:border-primary/40"}`}
                  >
                    {s.slot}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="overline">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="appt-type" className="mt-2 max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person"><MapPin size={14} className="inline mr-1.5"/>In-Person</SelectItem>
                  <SelectItem value="online"><Video size={14} className="inline mr-1.5"/>Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Reason / Concern</Label>
              <Textarea data-testid="appt-reason" rows={4} value={reason} onChange={(e)=>setReason(e.target.value)}
                placeholder="Briefly describe your symptoms, doshas, or area of concern…" className="mt-2 bg-transparent"/>
            </div>

            {/* Contact fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Full Name *</Label>
                <Input value={name} onChange={(e)=>setName(e.target.value)} className="mt-2 bg-transparent h-11" data-testid="appt-name"/>
              </div>
              <div>
                <Label className="overline">Phone *</Label>
                <Input type="tel" value={phone} onChange={(e)=>{setPhone(e.target.value); setPhoneToken("");}}
                  className="mt-2 bg-transparent h-11" data-testid="appt-phone" placeholder="+91 98xxxxxxxx"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="overline">Email (optional)</Label>
                <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="mt-2 bg-transparent h-11" data-testid="appt-email"/>
              </div>
            </div>

            {!user && (
              <PhoneVerify phone={phone} verified={Boolean(phoneToken)} onVerified={setPhoneToken}/>
            )}

            <Button onClick={book} disabled={busy || !canSubmit} className="btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-7" data-testid="appt-book-btn">
              {busy ? "Booking…" : "Confirm Appointment"}
            </Button>
          </CardContent>
        </Card>

        {user && (
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl mb-4">Your Appointments</h2>
            {mine.length === 0 && <p className="text-sm text-muted-foreground">No appointments yet.</p>}
            <div className="space-y-3">
              {mine.map((a) => (
                <Card key={a.id} className="border-border" data-testid={`my-appt-${a.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{a.date}</div>
                      <Badge variant={a.status === "confirmed" ? "default" : "secondary"}>{a.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock size={12}/>{a.time_slot}</span>
                      <span className="flex items-center gap-1">
                        {a.consultation_type === "online" ? <Video size={12}/> : <MapPin size={12}/>} {a.consultation_type}
                      </span>
                    </div>
                    <p className="text-sm mt-2 line-clamp-2">{a.reason}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
